import React, { useContext } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import CoreIcon from '@wix/da-ds/pkg/Icons/24x24/Core';
import EmptyState from '../../../EmptyState';
import { URLS } from '@wix/da-url/dist/src/constants';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiEventType, ReferralType } from '@wix/da-bi/pkg/constants';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import s from './SubscriptionsCoreOnlyBlock.scss';

export interface Props {
  className?: string;
  hasCore: boolean;
  createTier: () => void;
}

const SubscriptionsCoreOnlyBlock: React.FC<Props> = ({
  className,
  hasCore,
  createTier,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const updatedTiers = useFlag('updated_tier_cards');

  let variant;
  let cta;
  let secondaryCta;
  // eslint-disable-next-line default-case
  switch (true) {
    case !updatedTiers && isMobile:
      variant = 'mobile';
      break;
    case hasCore:
      variant = 'hasCore';
      cta = (
        <SolidButton
          variant="approval-green"
          size="medium"
          priority="primary"
          onClick={createTier}
          className={s['button']}
          biData={{
            evid: BiEventType.TIER_ADD_NEW,
            is_empty_state_view: 1,
            widgetname: undefined,
          }}
        >
          {t('page.subscriptions.coreOnlyBlock.CTAbutton.createTier')}
        </SolidButton>
      );
      break;
    case !hasCore:
      variant = 'noCore';
      cta = (
        <BrandedButton
          size="medium"
          variant="core-orange"
          href={URLS.CORE_MEMBERSHIP}
          className={s['button']}
          biData={{
            evid: BiEventType.BUY_CORE_BUTTON,
            referral_info: ReferralType.SUBSCRIPTIONS,
          }}
        >
          {t('page.subscriptions.coreOnlyBlock.CTAbutton.buyCore')}
        </BrandedButton>
      );
      secondaryCta = (
        <GhostButton
          variant="core-orange"
          size="medium"
          onClick={createTier}
          className={s['button']}
          biData={{
            evid: BiEventType.TIER_ADD_NEW,
            is_empty_state_view: 1,
            widgetname: undefined,
          }}
        >
          {t('page.subscriptions.coreOnlyBlock.CTAbutton.startCreatingATier')}
        </GhostButton>
      );
      break;
  }

  return (
    <EmptyState
      className={c(s['root'], className)}
      title={t(
        `page.subscriptions.coreOnlyBlock.title.var.${variant}${
          updatedTiers ? '.updatedTiers' : ''
        }`
      )}
      subtitle={
        <div className={s['subtitle-wrapper']}>
          <div className={s['subtitle']}>
            {t(
              `page.subscriptions.coreOnlyBlock.subtitle.var.${variant}${
                updatedTiers ? '.updatedTiers' : ''
              }`
            )}
          </div>
          {!updatedTiers && (
            <div className={s['subtitle-highlighted']}>
              <CoreIcon className={s['core-icon']} />
              {t(
                `page.subscriptions.coreOnlyBlock.subtitle.var.highlighted.${variant}`
              )}
            </div>
          )}
          {updatedTiers && hasCore && (
            <TextButton size="medium" variant="main">
              {t('page.subscriptions.coreOnlyBlock.CTAbutton.learnMore')}
            </TextButton>
          )}
        </div>
      }
      illustration={<div className={s['illustration']} />}
      withIllustrationBelow
      customButton={cta}
      customSecondaryButton={updatedTiers ? secondaryCta : undefined}
    />
  );
};
SubscriptionsCoreOnlyBlock.displayName = 'SubscriptionsCoreOnlyBlock';

export default SubscriptionsCoreOnlyBlock;
